import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  confirmInventoryListingAvailable,
  createInventoryFollowUp,
  fetchInventoryQualityQueue,
  requestInventoryListingConfirmation,
  resolveDuplicateCandidate,
  resolveOutlierFlag,
} from "../../../services/automapInventoryQualityApi";
import { runAdminListingAction } from "../../../services/automapDealerInventoryApi";
import { INCOMPLETE_ISSUE_GROUPS, trackInventoryQualityEvent } from "../../../constants/automapInventoryQualityConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  ConfirmDialog,
  DUPLICATE_STATUS_LABELS,
  formatCop,
  formatDate,
  InventoryQualityNav,
  IssueBadges,
  OUTLIER_FLAG_LABELS,
  QUEUE_META,
  queueFromPathname,
  STALE_SEVERITY_CLASS,
  STALE_SEVERITY_LABELS,
  vehicleSummary,
  qualityBadgeClass,
  formatListingStatus,
} from "./inventoryQualityShared";

function AdminInventoryQualityQueuePageContent() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = queueFromPathname(location.pathname) || "queue";
  const meta = QUEUE_META[queue];
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const issueGroup = searchParams.get("issueGroup") || "";

  const load = async () => {
    if (!meta) return;
    setLoading(true);
    setError("");
    try {
      const params = queue === "incomplete" && issueGroup ? { issueGroup } : {};
      const data = await fetchInventoryQualityQueue(queue, params);
      setItems(data.items || []);
      trackInventoryQualityEvent(meta.event);
    } catch (err) {
      setError(err.message || "No se pudo cargar la cola.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [queue, issueGroup]);

  const runListingAction = async (listingId, action) => {
    try {
      if (action === "confirm_available") {
        await confirmInventoryListingAvailable(listingId);
      } else if (action === "request_confirmation") {
        await requestInventoryListingConfirmation(listingId);
      } else {
        await runAdminListingAction(listingId, action);
      }
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    }
  };

  const resolveDuplicate = async (candidateId, resolution) => {
    try {
      await resolveDuplicateCandidate(candidateId, resolution);
      trackInventoryQualityEvent("duplicate_resolved", { resolution });
      await load();
    } catch (err) {
      setError(err.message || "No se pudo resolver duplicado.");
    }
  };

  const resolveOutlier = async (flagId, status) => {
    try {
      await resolveOutlierFlag(flagId, status);
      trackInventoryQualityEvent("outlier_resolved", { status });
      await load();
    } catch (err) {
      setError(err.message || "No se pudo resolver atípico.");
    }
  };

  const createFollowUp = async (dealerId, listingId) => {
    try {
      await createInventoryFollowUp({
        dealerId,
        listingId,
        followUpType: listingId ? "confirm_availability" : "update_inventory",
        message: "Seguimiento de calidad de inventario",
      });
      trackInventoryQualityEvent("inventory_followup_created", { dealerId, listingId });
      await load();
    } catch (err) {
      setError(err.message || "No se pudo crear seguimiento.");
    }
  };

  if (!meta) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Cola no reconocida." />
      </AdminPageLayout>
    );
  }

  const isPrioritized = queue === "queue";
  const listItems = isPrioritized ? [] : items;
  const prioritized = isPrioritized ? items : null;

  return (
    <AdminPageLayout className="admin-inventory-quality-page">
      <AdminAutomotiveNav active="inventory-quality" />
      <AdminPageHeader
        eyebrow="AutoMapa · Calidad"
        title={meta.title}
        description={meta.description}
        navigation={<InventoryQualityNav active={queue} />}
      />
      <main className="admin-console__main">
        {queue === "incomplete" ? (
          <div className="admin-filters" style={{ marginBottom: "1rem" }}>
            <select
              value={issueGroup}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set("issueGroup", e.target.value);
                else params.delete("issueGroup");
                setSearchParams(params);
              }}
            >
              {INCOMPLETE_ISSUE_GROUPS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
        ) : null}

        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Cargando cola…" />
        ) : isPrioritized ? (
          <div className="admin-listing-detail-grid">
            {["stale", "incomplete", "duplicates", "outliers"].map((key) => (
              <section key={key} className="admin-dashboard-card">
                <h3>{QUEUE_META[key]?.title || key}</h3>
                <Link to={`/admin/inventory-quality/${key}`}>Ver cola →</Link>
                <ul>
                  {(prioritized?.[key] || []).slice(0, 5).map((item) => (
                    <li key={item.id || item.listingIdA}>
                      {item.dealerName || item.listing?.dealerName || "—"} · {item.qualityScore ?? item.confidence ?? "—"}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : listItems.length === 0 ? (
          <AdminStatePanel variant="empty" message="No hay items en esta cola." />
        ) : queue === "duplicates" ? (
          <div className="admin-listing-detail-grid">
            {listItems.map((candidate) => (
              <section key={candidate.id} className="admin-dashboard-card">
                <h3>{DUPLICATE_STATUS_LABELS[candidate.duplicateStatus] || candidate.duplicateStatus}</h3>
                <p>Confianza: {Math.round((candidate.confidence || 0) * 100)}%</p>
                <div className="admin-dashboard__two-col">
                  <div>
                    <strong>Listing A</strong>
                    {candidate.listingA ? (
                      <>
                        <p>{vehicleSummary(candidate.listingA)}</p>
                        <p>{formatCop(candidate.listingA.priceCop)}</p>
                        <Link to={`/admin/listings/${candidate.listingIdA}`}>Ver</Link>
                      </>
                    ) : "—"}
                  </div>
                  <div>
                    <strong>Listing B</strong>
                    {candidate.listingB ? (
                      <>
                        <p>{vehicleSummary(candidate.listingB)}</p>
                        <p>{formatCop(candidate.listingB.priceCop)}</p>
                        <Link to={`/admin/listings/${candidate.listingIdB}`}>Ver</Link>
                      </>
                    ) : "—"}
                  </div>
                </div>
                {canManage ? (
                  <div className="dealer-inventory-actions" style={{ marginTop: "0.75rem", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => resolveDuplicate(candidate.id, "kept_both")}>Mantener ambos</button>
                    <button type="button" onClick={() => setConfirmAction({ type: "duplicate", candidateId: candidate.id, resolution: "archived_a" })}>Archivar A</button>
                    <button type="button" onClick={() => setConfirmAction({ type: "duplicate", candidateId: candidate.id, resolution: "archived_b" })}>Archivar B</button>
                    <button type="button" onClick={() => resolveDuplicate(candidate.id, "dismissed")}>Descartar</button>
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        ) : queue === "dealer-followups" ? (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Dealer</th><th>Ciudad</th><th>Owner</th><th>Obsoletos</th><th>Sin fotos</th><th>Calidad baja</th><th>Seguimientos</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((row) => (
                  <tr key={row.dealerId}>
                    <td><Link to={`/admin/dealers/${row.dealerId}`}>{row.dealerName}</Link></td>
                    <td>{row.city || "—"}</td>
                    <td>{row.assignedSalesOwner || "—"}</td>
                    <td>{row.staleListingCount}</td>
                    <td>{row.missingPhotoCount}</td>
                    <td>{row.lowQualityCount}</td>
                    <td>{row.openFollowUps}</td>
                    <td className="dealer-inventory-actions">
                      {row.whatsappUrl ? <a href={row.whatsappUrl} target="_blank" rel="noreferrer">WhatsApp</a> : null}
                      <Link to={`/admin/listings?dealerId=${row.dealerId}&stale=true`}>Ver obsoletos</Link>
                      {canManage ? <button type="button" onClick={() => createFollowUp(row.dealerId, null)}>Crear seguimiento</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        ) : queue === "outliers" ? (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr><th>Vehículo</th><th>Dealer</th><th>Tipo</th><th>Severidad</th><th>Detalle</th><th>Acciones</th></tr>
              </thead>
              <tbody>
                {listItems.map((flag) => (
                  <tr key={flag.id}>
                    <td>{flag.listing ? <Link to={`/admin/listings/${flag.listingId}`}>{vehicleSummary(flag.listing)}</Link> : flag.listingId}</td>
                    <td>{flag.listing?.dealerName || "—"}</td>
                    <td>{OUTLIER_FLAG_LABELS[flag.flagType] || flag.flagType}</td>
                    <td><span className={`admin-badge ${STALE_SEVERITY_CLASS[flag.severity] || ""}`}>{flag.severity}</span></td>
                    <td>{flag.reason}</td>
                    <td className="dealer-inventory-actions">
                      <Link to={`/admin/listings/${flag.listingId}/edit`}>Editar</Link>
                      {canManage ? (
                        <>
                          <button type="button" onClick={() => runListingAction(flag.listingId, "needs_review")}>Revisión</button>
                          <button type="button" onClick={() => resolveOutlier(flag.id, "dismissed")}>Descartar</button>
                          <button type="button" onClick={() => resolveOutlier(flag.id, "resolved")}>Resolver</button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        ) : (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Vehículo</th><th>Dealer</th>
                  {queue === "stale" ? <><th>Días</th><th>Severidad</th><th>WhatsApp</th></> : null}
                  <th>Calidad</th><th>Issues</th><th>Confirmado</th><th>Acción sugerida</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((listing) => (
                  <tr key={listing.id}>
                    <td><Link to={`/admin/listings/${listing.id}`}>{vehicleSummary(listing)}</Link></td>
                    <td><Link to={`/admin/dealers/${listing.dealerId}`}>{listing.dealerName}</Link></td>
                    {queue === "stale" ? (
                      <>
                        <td>{listing.daysStale ?? listing.daysSinceConfirm ?? "—"}</td>
                        <td>
                          {listing.staleSeverity ? (
                            <span className={`admin-badge ${STALE_SEVERITY_CLASS[listing.staleSeverity]}`}>
                              {STALE_SEVERITY_LABELS[listing.staleSeverity]}
                            </span>
                          ) : "—"}
                        </td>
                        <td>
                          {listing.dealerWhatsapp ? (
                            <a href={`https://wa.me/${listing.dealerWhatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">Contactar</a>
                          ) : "—"}
                        </td>
                      </>
                    ) : null}
                    <td>
                      <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>{listing.qualityScore}</span>
                      <div className="muted">{listing.qualityLabel}</div>
                    </td>
                    <td><IssueBadges issues={listing.qualityIssues} /></td>
                    <td>{formatDate(listing.lastConfirmedAt)}</td>
                    <td>{listing.recommendedAction || "—"}</td>
                    <td className="dealer-inventory-actions">
                      <Link to={`/admin/listings/${listing.id}/edit`}>Editar</Link>
                      {canManage ? (
                        <>
                          <button type="button" onClick={() => runListingAction(listing.id, "confirm_available")}>Confirmar</button>
                          <button type="button" onClick={() => runListingAction(listing.id, "request_confirmation")}>Solicitar dealer</button>
                          <button type="button" onClick={() => runListingAction(listing.id, "pause")}>Pausar</button>
                          <button type="button" onClick={() => createFollowUp(listing.dealerId, listing.id)}>Seguimiento</button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title="Confirmar acción"
        message="¿Confirmar esta acción?"
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction?.type === "duplicate") {
            resolveDuplicate(confirmAction.candidateId, confirmAction.resolution);
          }
          setConfirmAction(null);
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminInventoryQualityQueuePage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminInventoryQualityQueuePageContent />
    </RequirePermission>
  );
}
